/**
 * Sync Event Log
 *
 * Shows the append-only change_events log for this workspace.
 * Phase 1: read-only audit trail. Phase 2 will add conflict
 * review actions and dispatch controls.
 *
 * Route: /prioritization/settings/sync-log
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Filter,
  Loader2,
  RefreshCcw,
  ShieldAlert,
  XCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  Info,
  ExternalLink,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────

type SyncStatus = 'pending' | 'synced' | 'conflicted' | 'quarantined' | 'skipped';
type EventType =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'synced'
  | 'conflict_detected'
  | 'conflict_resolved'
  | 'rule_violated'
  | 'quarantined';

interface ChangeEvent {
  id: string;
  source: string;
  entityType: string;
  entityId: string;
  fieldName: string | null;
  oldValue: unknown;
  newValue: unknown;
  eventType: EventType;
  syncStatus: SyncStatus;
  conflictData: unknown;
  externalId: string | null;
  createdAt: string;
}

// ── Helpers ───────────────────────────────────────────────────

const STATUS_STYLES: Record<SyncStatus, { label: string; bg: string; text: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pending',
    bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20',
    text: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
    icon: <Clock className="h-3 w-3" />,
  },
  synced: {
    label: 'Synced',
    bg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/20',
    text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  conflicted: {
    label: 'Conflict',
    bg: 'bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20',
    text: 'text-[var(--coral)] dark:text-[var(--coral)]',
    icon: <AlertTriangle className="h-3 w-3" />,
  },
  quarantined: {
    label: 'Quarantined',
    bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20',
    text: 'text-[var(--text-sec)]',
    icon: <ShieldAlert className="h-3 w-3" />,
  },
  skipped: {
    label: 'Skipped',
    bg: 'bg-[var(--hover-tint)]',
    text: 'text-[var(--text-ter)]',
    icon: <XCircle className="h-3 w-3" />,
  },
};

const EVENT_TYPE_STYLES: Record<EventType, string> = {
  created: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  updated: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
  deleted: 'text-[var(--coral)] dark:text-[var(--coral)]',
  synced: 'text-[var(--text-ter)]',
  conflict_detected: 'text-[var(--coral)] dark:text-[var(--coral)]',
  conflict_resolved: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  rule_violated: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
  quarantined: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
};

const SOURCE_LABEL: Record<string, string> = {
  priorities_ai: 'priorities.ai',
  jira: 'Jira',
  linear: 'Linear',
  github: 'GitHub',
  miro: 'Miro',
  slack: 'Slack',
  asana: 'Asana',
  notion: 'Notion',
  csv: 'CSV Import/Export',
};

function formatTimestamp(iso: string) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length > 60 ? v.slice(0, 60) + '…' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v).slice(0, 80);
}

// ── Sub-components ────────────────────────────────────────────

function StatusBadge({ status }: { status: SyncStatus }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.icon}
      {s.label}
    </span>
  );
}

function EventRow({ event }: { event: ChangeEvent }) {
  const [expanded, setExpanded] = useState(false);
  const sourceLabel = SOURCE_LABEL[event.source] ?? event.source;
  const isInternal = event.source === 'priorities_ai';

  return (
    <div className="border-b border-[var(--rule)] last:border-0">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/50 transition-colors text-left"
      >
        {/* Expand toggle */}
        <span className="flex-shrink-0 text-[var(--text-ter)]">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </span>

        {/* Source badge */}
        <span className={`flex-shrink-0 flex items-center gap-1 text-xs font-medium px-2 py-0.5  ${
          isInternal
            ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 text-[var(--text-sec)]'
            : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
        }`}>
          {isInternal
            ? <ArrowUpFromLine className="h-3 w-3" />
            : <ArrowDownToLine className="h-3 w-3" />}
          {sourceLabel}
        </span>

        {/* Entity type + event type */}
        <span className="flex-shrink-0 text-xs text-[var(--text-ter)]">
          {event.entityType}
        </span>
        <span className={`flex-shrink-0 text-xs font-medium ${EVENT_TYPE_STYLES[event.eventType] ?? ''}`}>
          {event.eventType.replace(/_/g, ' ')}
        </span>

        {/* Field name (if update) */}
        {event.fieldName && (
          <span className="flex-shrink-0 text-xs text-[var(--text-ter)]">
            · {event.fieldName}
          </span>
        )}

        {/* New value preview */}
        {event.newValue !== null && event.newValue !== undefined && (
          <span className="flex-1 min-w-0 text-xs text-[var(--text-sec)] truncate">
            → {formatValue(event.newValue)}
          </span>
        )}

        {/* Status + timestamp */}
        <span className="flex-shrink-0"><StatusBadge status={event.syncStatus} /></span>
        <span className="flex-shrink-0 text-xs text-[var(--text-ter)] whitespace-nowrap">
          {formatTimestamp(event.createdAt)}
        </span>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 space-y-3 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/30">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-[var(--text-ter)] uppercase tracking-wide text-[10px]">Entity ID</span>
              <p className="font-mono text-[var(--text-sec)] break-all mt-0.5">{event.entityId}</p>
            </div>
            <div>
              <span className="text-[var(--text-ter)] uppercase tracking-wide text-[10px]">External ID</span>
              <p className="font-mono text-[var(--text-sec)] break-all mt-0.5">{event.externalId ?? '—'}</p>
            </div>
            {event.oldValue !== null && event.oldValue !== undefined && (
              <div>
                <span className="text-[var(--text-ter)] uppercase tracking-wide text-[10px]">Old value</span>
                <p className="font-mono text-[var(--text-sec)] mt-0.5 break-words">
                  {formatValue(event.oldValue)}
                </p>
              </div>
            )}
            {event.newValue !== null && event.newValue !== undefined && (
              <div>
                <span className="text-[var(--text-ter)] uppercase tracking-wide text-[10px]">New value</span>
                <p className="font-mono text-[var(--text-sec)] mt-0.5 break-words">
                  {formatValue(event.newValue)}
                </p>
              </div>
            )}
          </div>

          {/* Conflict data */}
          {event.conflictData && (
            <div className="p-2 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]">
              <p className="text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)] mb-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Conflict data
              </p>
              <pre className="text-xs text-[var(--coral)] dark:text-[var(--coral)] overflow-auto whitespace-pre-wrap">
                {JSON.stringify(event.conflictData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

const STATUS_FILTERS: { value: SyncStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'synced', label: 'Synced' },
  { value: 'conflicted', label: 'Conflicts' },
  { value: 'quarantined', label: 'Quarantined' },
  { value: 'skipped', label: 'Skipped' },
];

export default function SyncEventLog() {
  const [events, setEvents] = useState<ChangeEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SyncStatus | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const workspaceId = getCurrentWorkspaceId();

  const load = useCallback(async (reset = false) => {
    if (!workspaceId) {
      setError('No workspace context');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const supabase = getSafeSupabaseClient();
      const currentPage = reset ? 0 : page;
      let query = supabase
        .from('change_events')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .range(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE - 1);

      if (statusFilter !== 'all') query = query.eq('sync_status', statusFilter);
      if (sourceFilter !== 'all') query = query.eq('source', sourceFilter);

      const { data, error: dbErr } = await query;
      if (dbErr) throw new Error(dbErr.message);

      const mapped: ChangeEvent[] = (data ?? []).map((row) => ({
        id: row.id as string,
        source: row.source as string,
        entityType: row.entity_type as string,
        entityId: row.entity_id as string,
        fieldName: row.field_name as string | null,
        oldValue: row.old_value,
        newValue: row.new_value,
        eventType: row.event_type as EventType,
        syncStatus: row.sync_status as SyncStatus,
        conflictData: row.conflict_data,
        externalId: row.external_id as string | null,
        createdAt: row.created_at as string,
      }));

      if (reset) {
        setEvents(mapped);
        setPage(0);
      } else {
        setEvents((prev) => (currentPage === 0 ? mapped : [...prev, ...mapped]));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, statusFilter, sourceFilter, page]);

  useEffect(() => { load(true); }, [statusFilter, sourceFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Count by status from current page for summary bar
  const statusCounts = events.reduce<Record<string, number>>((acc, e) => {
    acc[e.syncStatus] = (acc[e.syncStatus] ?? 0) + 1;
    return acc;
  }, {});

  const sources = Array.from(new Set(events.map((e) => e.source)));

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ArrowLeftRight className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Sync Event Log"
        subtitle="Append-only record of every change that passes through the sync layer — from priorities.ai and from connected external systems."
      />

      {/* Phase 2 info banner — with conflict queue shortcut */}
      <div className="flex items-start justify-between gap-3 px-4 py-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] text-sm text-[var(--text-sec)]">
        <div className="flex items-start gap-3">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">Phase 2 — Dispatch + conflict resolution.</span>{' '}
            Events from priorities.ai are dispatched outbound via the{' '}
            <code className="text-xs bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 px-1 py-0.5 rounded font-mono">/dispatch</code>{' '}
            edge function. Inbound conflicts are reviewed in the Conflict Queue.
          </div>
        </div>
        {(statusCounts['conflicted'] ?? 0) + (statusCounts['quarantined'] ?? 0) > 0 && (
          <Link
            to="/prioritization/settings/conflict-queue"
            className="flex-shrink-0 flex items-center gap-1 text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] whitespace-nowrap"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            {(statusCounts['conflicted'] ?? 0) + (statusCounts['quarantined'] ?? 0)} to review
            <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-5 gap-3">
        {(['synced', 'pending', 'conflicted', 'quarantined', 'skipped'] as SyncStatus[]).map((s) => {
          const style = STATUS_STYLES[s];
          const count = statusCounts[s] ?? 0;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
              className={`p-3  border text-left transition-all ${
                statusFilter === s
                  ? `${style.bg} border-current ring-1 ring-inset ${style.text}`
                  : 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-[var(--rule)] hover:border-[var(--rule)]'
              }`}
            >
              <div className={`text-2xl font-semibold ${count > 0 && (s === 'conflicted' || s === 'quarantined') ? style.text : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}>
                {count}
              </div>
              <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 capitalize">{style.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filters + refresh */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />

        {/* Status filter tabs */}
        <div className="flex items-center gap-1 p-1 bg-[var(--hover-tint)]">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value as SyncStatus | 'all')}
              className={`px-3 py-1.5 text-xs font-medium  transition-colors ${
                statusFilter === f.value
                  ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] shadow-sm'
                  : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Source filter */}
        {sources.length > 1 && (
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-1.5 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All sources</option>
            {sources.map((s) => (
              <option key={s} value={s}>{SOURCE_LABEL[s] ?? s}</option>
            ))}
          </select>
        )}

        <div className="flex-1" />

        <button
          onClick={() => load(true)}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] disabled:opacity-50 transition-colors"
        >
          {isLoading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCcw className="h-3.5 w-3.5" />}
          Refresh
        </button>
      </div>

      {/* Event list */}
      {error ? (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      ) : events.length === 0 && !isLoading ? (
        <div className="text-center py-16 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Activity className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No events yet</p>
          <p className="text-xs mt-1">
            Events will appear here when changes are made in priorities.ai or received from connected systems.
          </p>
        </div>
      ) : (
        <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
          {/* Header row */}
          <div className="flex items-center gap-3 px-4 py-2 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 border-b border-[var(--rule)] text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
            <span className="w-4" />
            <span className="w-20">Source</span>
            <span className="w-24">Entity</span>
            <span className="w-20">Event</span>
            <span className="flex-1">Value</span>
            <span className="w-24">Status</span>
            <span className="w-36 text-right">Time</span>
          </div>

          {isLoading && events.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-[var(--text-ter)]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading events…
            </div>
          ) : (
            <>
              {events.map((e) => <EventRow key={e.id} event={e} />)}

              {/* Load more */}
              {events.length >= PAGE_SIZE && (
                <div className="px-4 py-3 border-t border-[var(--rule)] text-center">
                  <button
                    onClick={() => {
                      setPage((p) => p + 1);
                      load(false);
                    }}
                    disabled={isLoading}
                    className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" /> : null}
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
